import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as d3 from 'd3';
import { lightTheme as T } from '../../Constants/Colors';
import { Body } from '../../Constants/Fonts';
import ChartTooltip from '../../Components/ChartTooltip';

// Risk Trend (Last 90 Days) — an overlaid area chart of how many monitored
// vendors sit at each risk level (Low / Medium / High / Critical) across the
// window. It plots the SAME portfolio data the grade distribution carries,
// just as a trend rather than a bar (grade → risk level: A/B = Low, C =
// Medium, D = High, F = Critical). The backend exposes no per-day history, so
// the parent feeds a `series` of dated points it derives from the current
// snapshot; the axis still shows real dates so the card reads as a timeline.

// One entry per risk level, ordered so the largest bands draw first (behind).
const LEVELS = [
    { key: 'medium', label: 'Medium', color: T.warning },
    { key: 'low', label: 'Low', color: T.success },
    { key: 'high', label: 'High', color: T.danger },
    { key: 'critical', label: 'Critical', color: T.textPrimary },
];

// Legend order matches the reference design (Low → Medium → High → Critical).
const LEGEND = ['low', 'medium', 'high', 'critical'].map(
    (key) => LEVELS.find((l) => l.key === key),
);

const MARGIN = { top: 16, right: 16, bottom: 28, left: 32 };

function RiskTrendChart({ series = [], height = 300 }) {
    const HEIGHT = height;
    const containerRef = useRef(null);
    const [width, setWidth] = useState(0);
    // Index of the series point the cursor is nearest, or null when not hovering.
    const [hoverIdx, setHoverIdx] = useState(null);

    // Redraw when the card itself changes width (sidebar toggle, sibling
    // resize, first paint) — not only on window resize. Ignore 0-width
    // readings that happen mid-layout.
    useEffect(() => {
        if (typeof ResizeObserver === 'undefined') {
            if (containerRef.current) setWidth(containerRef.current.offsetWidth);
            return;
        }
        const ro = new ResizeObserver((entries) => {
            const w = entries[0]?.contentRect?.width;
            if (w) setWidth(w);
        });
        if (containerRef.current) ro.observe(containerRef.current);
        return () => ro.disconnect();
    }, []);

    const { paths, xTicks, yTicks, pts, innerW, innerH } = useMemo(() => {
        const innerW = Math.max(width - MARGIN.left - MARGIN.right, 0);
        const innerH = HEIGHT - MARGIN.top - MARGIN.bottom;
        if (!innerW || !series.length) {
            return { paths: [], xTicks: [], yTicks: [], pts: [], innerW: 0, innerH };
        }

        const x = d3.scaleTime()
            .domain(d3.extent(series, (d) => d.date))
            .range([0, innerW]);

        const maxY = d3.max(series, (d) => Math.max(d.low, d.medium, d.high, d.critical)) || 1;
        const y = d3.scaleLinear()
            .domain([0, maxY * 1.25])
            .nice()
            .range([innerH, 0]);

        const areaGen = (key) => d3.area()
            .x((d) => x(d.date))
            .y0(innerH)
            .y1((d) => y(d[key]))
            .curve(d3.curveMonotoneX);

        const lineGen = (key) => d3.line()
            .x((d) => x(d.date))
            .y((d) => y(d[key]))
            .curve(d3.curveMonotoneX);

        const drawn = LEVELS.map((lvl) => ({
            ...lvl,
            area: areaGen(lvl.key)(series),
            line: lineGen(lvl.key)(series),
        }));

        // One hover anchor per data point: its px x, the topmost line's y (so the
        // marker/tooltip sit above the bands), the date, and each level's y+value.
        const pts = series.map((d) => {
            const dots = LEVELS.map((lvl) => ({ ...lvl, cy: y(d[lvl.key]), value: d[lvl.key] }));
            return {
                cx: x(d.date),
                topY: Math.min(...dots.map((dt) => dt.cy)),
                date: d.date,
                dots,
            };
        });

        return {
            paths: drawn,
            xTicks: x.ticks(6).map((t) => ({ x: x(t), label: d3.timeFormat('%b %d')(t) })),
            yTicks: y.ticks(4).map((t) => ({ y: y(t), label: t })),
            pts,
            innerW,
            innerH,
        };
    }, [series, width, HEIGHT]);

    // Map the cursor's x (relative to the plot's inner area) to the nearest
    // data point, so the guide/markers snap to real points rather than floating.
    const handleMove = (e) => {
        if (!pts.length) return;
        const rect = e.currentTarget.getBoundingClientRect();
        const mx = e.clientX - rect.left; // already inside the translated <g>
        let nearest = 0;
        let best = Infinity;
        pts.forEach((p, i) => {
            const dist = Math.abs(p.cx - mx);
            if (dist < best) { best = dist; nearest = i; }
        });
        setHoverIdx(nearest);
    };

    const hovered = hoverIdx != null ? pts[hoverIdx] : null;

    return (
        <div ref={containerRef} style={{ width: '100%', position: 'relative' }}>
            {/* Legend */}
            <div style={{ display: 'flex', gap: 18, justifyContent: 'flex-end', flexWrap: 'wrap', marginBottom: 8 }}>
                {LEGEND.map((lvl) => (
                    <span key={lvl.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ width: 10, height: 10, borderRadius: '50%', background: lvl.color, display: 'inline-block' }} />
                        <Body as="span" variant={2} color="var(--color-text-secondary)">{lvl.label}</Body>
                    </span>
                ))}
            </div>

            {width > 0 && series.length > 0 ? (
                <div style={{ position: 'relative' }}>
                    <svg width={width} height={HEIGHT} role="img" aria-label="Risk trend over the last 90 days">
                        <g transform={`translate(${MARGIN.left},${MARGIN.top})`}>
                            {/* Y-axis labels only — no gridlines, per design. */}
                            {yTicks.map((t, i) => (
                                <text key={i} x={-8} y={t.y} dy="0.32em" textAnchor="end" fontSize={11} fill={T.textMuted}>
                                    {t.label}
                                </text>
                            ))}

                            {/* Areas (largest bands drawn first) + top lines */}
                            {paths.map((p) => (
                                <path key={`a-${p.key}`} d={p.area} fill={p.color} fillOpacity={0.18} />
                            ))}
                            {paths.map((p) => (
                                <path key={`l-${p.key}`} d={p.line} fill="none" stroke={p.color} strokeWidth={2} />
                            ))}

                            {/* X labels */}
                            {xTicks.map((t, i) => (
                                <text
                                    key={i}
                                    x={t.x}
                                    y={HEIGHT - MARGIN.top - MARGIN.bottom + 18}
                                    textAnchor="middle"
                                    fontSize={11}
                                    fill={T.textMuted}
                                >
                                    {t.label}
                                </text>
                            ))}

                            {/* Hover guide line + a marker on each band at the hovered point */}
                            {hovered && (
                                <g style={{ pointerEvents: 'none' }}>
                                    <line
                                        x1={hovered.cx}
                                        x2={hovered.cx}
                                        y1={0}
                                        y2={innerH}
                                        stroke={T.border}
                                        strokeWidth={1}
                                        strokeDasharray="3 3"
                                    />
                                    {hovered.dots.map((d) => (
                                        <circle
                                            key={d.key}
                                            cx={hovered.cx}
                                            cy={d.cy}
                                            r={4}
                                            fill={T.surface}
                                            stroke={d.color}
                                            strokeWidth={2}
                                        />
                                    ))}
                                </g>
                            )}

                            {/* Transparent capture layer — snaps the tooltip to the
                                nearest point on move, clears it on leave. Drawn last
                                so it receives the pointer events. */}
                            <rect
                                x={0}
                                y={0}
                                width={innerW}
                                height={innerH}
                                fill="transparent"
                                onMouseMove={handleMove}
                                onMouseLeave={() => setHoverIdx(null)}
                            />
                        </g>
                    </svg>

                    <ChartTooltip
                        visible={!!hovered}
                        x={hovered ? MARGIN.left + hovered.cx : 0}
                        y={hovered ? MARGIN.top + hovered.topY : 0}
                        align={hovered && hovered.cx > innerW / 2 ? 'right' : 'left'}
                        title={hovered ? d3.timeFormat('%b %d, %Y')(hovered.date) : ''}
                        items={hovered ? LEGEND.map((lvl) => {
                            const dot = hovered.dots.find((d) => d.key === lvl.key);
                            return { label: lvl.label, value: dot ? dot.value : 0, color: lvl.color };
                        }) : []}
                    />
                </div>
            ) : (
                <div style={{ height: HEIGHT, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Body color="var(--color-text-muted)">No risk data to plot yet.</Body>
                </div>
            )}
        </div>
    );
}

export default RiskTrendChart;
